import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeocodeSuggestResultDc } from "@evergis/api";
import { useEvergisContext, useMapContext } from "@evergis/react";
import { IconButton, IconTypesKeys, IOption } from "@evergis/ui";
import { isEmpty } from "lodash";
import { AddressProviderSelect } from "./AddressProviderSelect";

import { AutoComplete } from "./styled";
import {
  GEOCODE_PROVIDER,
  GEOCODE_PROVIDER_CADASTR,
  GEOCODE_PROVIDER_ORGANIZATIONS,
} from "../../../../resources/geocode";
import { fixProjectPosition, getBbox } from "../../../../hooks/preview/useGeneratePreview";
import { useGeocode } from "../../hooks/useGeocode";
import { useAddressCoordinates } from "../../hooks/useAddressCoordinates";
import { useProjectSrid } from "../../../../ducks/project/hooks";

type GeocodeLabel = { value: string; text: string; description: string };

const createGeocodeLabel = ({ text, label }: GeocodeSuggestResultDc): GeocodeLabel => ({
  value: text,
  text,
  description: label,
});

const formatSuggests = (suggests: GeocodeSuggestResultDc[]): GeocodeLabel[] =>
  Object.values(
    suggests.reduce<Record<string, GeocodeLabel>>(
      (suggestsMap, suggest) =>
        suggestsMap[suggest.text] || isEmpty(suggest)
          ? suggestsMap
          : {
              ...suggestsMap,
              [suggest.text]: createGeocodeLabel(suggest),
            },
      {},
    ),
  );

export const AddressSearchButton: FC = () => {
  const { geocode } = useEvergisContext();
  const [address, setAddress] = useState("");
  const input = useRef<HTMLElement>(null);
  const [inputShowed, showInput] = useState(false);
  const [providerName, setProviderName] = useState(GEOCODE_PROVIDER);
  const [, { updateGeocodeAddress, clearAddress }] = useGeocode(providerName);
  const { isCoordinates, getCoordinates, geolocateToCoordinates } = useAddressCoordinates(providerName);
  const { t } = useTranslation("mapControls");
  const { map } = useMapContext();
  const sr = useProjectSrid();

  const providerOptions = useMemo(
    () => [
      {
        text: t("addressAndCoordinates"),
        value: GEOCODE_PROVIDER,
        icon: "search" as IconTypesKeys,
      },
      {
        text: t("cadastralNumber"),
        value: GEOCODE_PROVIDER_CADASTR,
        icon: "cadastr" as IconTypesKeys,
      },
      {
        text: t("organizations"),
        value: GEOCODE_PROVIDER_ORGANIZATIONS,
        icon: "office" as IconTypesKeys,
      },
    ],
    [t],
  );
  const { icon } = providerOptions.find(({ value }) => value === providerName) || providerOptions[0];

  const clearInput = useCallback(() => {
    clearAddress();
    setAddress("");
  }, [clearAddress]);

  const toggleSearchBar = useCallback(() => {
    showInput(!inputShowed);

    if (inputShowed) {
      clearInput();
    } else if (input.current) {
      input.current.focus();
    }
  }, [inputShowed, clearInput]);

  const onAsyncGetSuggests = useCallback(
    async (value: string) => {
      if (value && !isCoordinates(value)) {
        const bbox = getBbox(fixProjectPosition(map.position), map.resolution, sr);
        const suggestions = await geocode.suggest({
          providerName,
          address: value,
          bboxPoints: bbox.coordinates,
          sr,
        });

        return formatSuggests(suggestions);
      }

      return [];
    },
    [geocode, isCoordinates, map.position, map.resolution, sr, providerName],
  );

  const updateAddress = useCallback(
    (addr: string) => {
      if (!addr) return;

      const coordinates = getCoordinates(addr);

      if (coordinates.length) {
        geolocateToCoordinates(coordinates);

        return;
      }

      updateGeocodeAddress(addr);
    },
    [geolocateToCoordinates, getCoordinates, updateGeocodeAddress],
  );

  return (
    <>
      <IconButton title={t("addressSearch")} primary={inputShowed} kind={icon} onClick={toggleSearchBar} />
      <AddressProviderSelect
        providerOptions={providerOptions}
        providerName={providerName}
        setProviderName={item => {
          setProviderName(item);
          showInput(true);
        }}
      />
      <AutoComplete
        options={[]}
        value={address}
        bottomBind={false}
        asyncCallback={onAsyncGetSuggests}
        onChange={setAddress}
        onSelect={({ text }) => updateAddress(text)}
        zIndex={999}
        isVisible={inputShowed}
        menuHeight={"10rem"}
        width={inputShowed ? "18rem" : "0"}
        style={{ opacity: +inputShowed, paddingRight: `${inputShowed ? 2 : 0}rem` }}
        placeholder={t("addressSearchPlaceholder")}
        onKeyPress={e => e.key === "Enter" && updateAddress(address)}
        filterOptions={(value: string, options: IOption[]) =>
          options.slice().sort((a, b) => (b.text.toLowerCase().includes(value || "") ? 1 : -1))
        }
        onGetInnerRef={ref => (input.current = ref)}
      />
      {address && inputShowed && (
        <IconButton kind={"close"} style={{ position: "absolute", right: ".25rem" }} onClick={clearInput} />
      )}
    </>
  );
};
